import { API } from 'aws-amplify';
import { Area } from '../components/InteractiveMap/InteractiveMap.types';

// The listAreas query used by lyferize
const listAreas = `
  query ListAreas($showCompleted: Boolean!, $size: Int, $searchAfter: String) {
    listAreas(
      showCompleted: $showCompleted
      size: $size
      searchAfter: $searchAfter
    )
  }
`;

// The createArea mutation used by lyferize for all CRUD operations
const createArea = `
  mutation CreateArea($request: AWSJSON!) {
    createArea(request: $request)
  }
`;

export interface AreaResponse {
  id: string;
  name: string;
  description?: string;
  coords?: Array<{ lat: number; lon: number }>;
  points?: Array<{ lat: number; lon: number }>;
  users?: any[];
  circles?: any[];
  createdBy?: string;
  lastUpdatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  isDeleted?: boolean;
  // Add other fields that might be in the areas response
  [key: string]: any;
}

export const fetchServiceAreas = async (): Promise<Area[]> => {
  try {
    // @ts-ignore - global.me is available in the application
    const orgID = global.me?.orgID;

    if (!orgID) {
      console.error('No organization ID found');
      return [];
    }

    let searchAfter: string | undefined;
    const size = 50;
    let done = false;
    let allAreasData: AreaResponse[] = [];

    // Fetch areas with pagination
    do {
      const variables: any = {
        showCompleted: true,
        size,
      };

      if (searchAfter) {
        variables.searchAfter = searchAfter;
      }

      const response: any = await API.graphql({
        query: listAreas,
        variables,
      });

      if (response.data.listAreas) {
        const parsedData = JSON.parse(response.data.listAreas);
        const areasList = parsedData.areas || [];

        if (areasList.length > 0) {
          allAreasData = [...allAreasData, ...areasList];
          searchAfter = areasList[areasList.length - 1].id;
          done = areasList.length < size;
        } else {
          done = true;
        }
      } else {
        done = true;
      }
    } while (!done);

    // Transform and filter areas
    const transformedAreas: Area[] = allAreasData
      .filter((area) => !area.isDeleted) // Filter out deleted areas
      .map((area) => ({
        id: area.id,
        name: area.name || `Area ${area.id.slice(-6)}`,
        description: area.description,
        coords: area.coords || area.points || [], // Handle both coords and points
        users: area.users || [],
        circles: area.circles || [],
        colorCode: area.colorcode || area.colorCode,
        createdBy: area.createdBy,
        lastUpdatedBy: area.lastUpdatedBy,
        createdAt: area.createdAt,
        updatedAt: area.updatedAt,
        isDeleted: area.isDeleted,
      }))
      .filter((area) => area.coords && area.coords.length > 0); // Only include areas with coordinates

    return transformedAreas;
  } catch (error) {
    console.error('Error fetching service areas:', error);
    throw error;
  }
};

export interface UpdateAreaInput {
  id: string;
  name?: string;
  coords?: Array<{ lat: number; lon: number }>;
  users?: any[];
  circles?: any[];
}

export const updateArea = async (
  input: UpdateAreaInput,
  originalArea?: Area
): Promise<Area> => {
  try {
    console.log('Updating area:', input);

    // @ts-ignore - global.me is available in the application
    const orgID = global.me?.orgID;
    // @ts-ignore - global.me is available in the application
    const currentUser = global.me?.id;

    // Calculate add_ids and remove_ids for incremental updates
    let addIds: string[] = [];
    let removeIds: string[] = [];
    let assignedUsers: string[] = [];
    let assignedCircles: string[] = [];

    if (input.users !== undefined || input.circles !== undefined) {
      // Get current user and circle IDs
      const currentUserIds = (originalArea?.users || []).map((u) =>
        typeof u === 'string' ? u : u.id
      );
      const currentCircleIds = (originalArea?.circles || []).map((c) =>
        typeof c === 'string' ? c : c.id
      );

      // Get new user and circle IDs
      const newUserIds = (input.users || originalArea?.users || []).map((u) =>
        typeof u === 'string' ? u : u.id
      );
      const newCircleIds = (input.circles || originalArea?.circles || []).map(
        (c) => (typeof c === 'string' ? c : c.id)
      );

      // Calculate users to add and remove
      const usersToAdd = newUserIds.filter(
        (id) => !currentUserIds.includes(id)
      );
      const usersToRemove = currentUserIds.filter(
        (id) => !newUserIds.includes(id)
      );

      // Calculate circles to add and remove
      const circlesToAdd = newCircleIds.filter(
        (id) => !currentCircleIds.includes(id)
      );
      const circlesToRemove = currentCircleIds.filter(
        (id) => !newCircleIds.includes(id)
      );

      // Combine all changes
      addIds = [...usersToAdd, ...circlesToAdd];
      removeIds = [...usersToRemove, ...circlesToRemove];
      assignedUsers = newUserIds;
      assignedCircles = newCircleIds;

      console.log('Assignment changes:', {
        currentUserIds,
        currentCircleIds,
        newUserIds,
        newCircleIds,
        usersToAdd,
        usersToRemove,
        circlesToAdd,
        circlesToRemove,
        addIds,
        removeIds,
      });
    }

    // Format parameters like lyferize does
    const params = {
      id: input.id,
      orgID,
      ...(input.name !== undefined && { name: input.name }),
      ...(input.coords !== undefined && { points: input.coords }), // lyferize uses 'points' not 'coords'
      ...((assignedUsers.length > 0 ||
        assignedCircles.length > 0 ||
        addIds.length > 0 ||
        removeIds.length > 0) && {
        assignedUsers,
        assignedCircles,
        add_ids: addIds,
        remove_ids: removeIds,
      }),
      lastUpdatedBy: currentUser,
    };

    // Double stringify like lyferize does
    const stringifiedParams = JSON.stringify(params);
    console.log('Formatted params:', params);
    console.log('Stringified params:', stringifiedParams);

    const response: any = await API.graphql({
      query: createArea,
      variables: {
        request: JSON.stringify(stringifiedParams),
      },
    });

    if (response.data?.createArea) {
      // Parse the response like lyferize does
      const createAreaResult = response.data.createArea;
      const finalResult = JSON.parse(createAreaResult);

      // Check if this is a success response without area data
      if (finalResult.statusCode === 200 && finalResult.message) {
        // Since the API doesn't return the updated area, we construct it from our input
        // Merge with original area data if available
        const transformedArea: Area = {
          id: input.id,
          name:
            input.name ?? originalArea?.name ?? `Area ${input.id.slice(-6)}`,
          description: originalArea?.description ?? '',
          coords: input.coords ?? originalArea?.coords ?? [],
          users: input.users ?? originalArea?.users ?? [],
          circles: input.circles ?? originalArea?.circles ?? [],
          colorCode: originalArea?.colorcode || originalArea?.colorCode,
          createdBy: originalArea?.createdBy,
          lastUpdatedBy: currentUser,
          createdAt: originalArea?.createdAt,
          updatedAt: new Date().toISOString(),
          isDeleted: originalArea?.isDeleted ?? false,
        };

        return transformedArea;
      }

      // Handle case where actual area data is returned
      const updatedArea = finalResult.updatedArea || finalResult;

      // Check if updatedArea exists and has required fields
      if (!updatedArea || !updatedArea.id) {
        console.error('Invalid updatedArea structure:', updatedArea);
        throw new Error(
          'Invalid response structure: missing updatedArea or id'
        );
      }

      // Transform to our Area interface
      const transformedArea: Area = {
        id: updatedArea.id,
        name: updatedArea.name || `Area ${updatedArea.id.slice(-6)}`,
        description: updatedArea.description,
        coords: updatedArea.coords || updatedArea.points || [], // Handle both formats
        users: updatedArea.users || updatedArea.assignedUsers || [],
        circles: updatedArea.circles || updatedArea.assignedCircles || [],
        colorCode: updatedArea.colorcode || updatedArea.colorCode,
        createdBy: updatedArea.createdBy,
        lastUpdatedBy: updatedArea.lastUpdatedBy,
        createdAt: updatedArea.createdAt,
        updatedAt: updatedArea.updatedAt,
        isDeleted: updatedArea.isDeleted,
      };

      return transformedArea;
    } else {
      console.error('No createArea data in response:', response);
      throw new Error('No data returned from createArea mutation');
    }
  } catch (error) {
    console.error('Error updating area:', error);
    throw error;
  }
};

export interface CreateAreaInput {
  name: string;
  coords: Array<{ lat: number; lon: number }>;
  description?: string;
}

export const createNewArea = async (input: CreateAreaInput): Promise<Area> => {
  try {
    console.log('Creating new area:', input);

    // @ts-ignore - global.me is available in the application
    const orgID = global.me?.orgID;
    // @ts-ignore - global.me is available in the application
    const currentUser = global.me?.id;

    if (!orgID) {
      throw new Error('No organization ID found');
    }

    // Format parameters like lyferize does - let backend generate the ID
    const params = {
      id: null, // Let backend generate the ID
      orgID,
      name: input.name,
      points: input.coords, // lyferize uses 'points' not 'coords'
      description: input.description || '',
      assignedUsers: [],
      assignedCircles: [],
      add_ids: [],
      remove_ids: [],
      createdBy: currentUser,
      lastUpdatedBy: currentUser,
      isDeleted: false,
    };

    // Double stringify like lyferize does
    const stringifiedParams = JSON.stringify(params);
    console.log('Formatted params for new area:', params);
    console.log('Stringified params for new area:', stringifiedParams);

    const response: any = await API.graphql({
      query: createArea,
      variables: {
        request: JSON.stringify(stringifiedParams),
      },
    });

    if (response.data?.createArea) {
      // Parse the response like lyferize does
      const createAreaResult = response.data.createArea;
      const finalResult = JSON.parse(createAreaResult);

      // Check if this is a success response without area data
      if (finalResult.statusCode === 200 && finalResult.message) {
        // Since the API doesn't return the created area, we construct it from our input
        // Use a temporary ID that will be replaced when we reload areas
        const tempId = `temp-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`;
        const transformedArea: Area = {
          id: tempId,
          name: input.name,
          description: input.description || '',
          coords: input.coords,
          users: [],
          circles: [],
          colorCode: '#D0D2D8', // Default gray for new areas with no assignees
          createdBy: currentUser,
          lastUpdatedBy: currentUser,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isDeleted: false,
        };

        return transformedArea;
      }

      // Handle case where actual area data is returned
      const createdArea = finalResult.newArea || finalResult;

      // Check if createdArea exists and has required fields
      if (!createdArea || !createdArea.id) {
        console.error('Invalid createdArea structure:', createdArea);
        throw new Error(
          'Invalid response structure: missing createdArea or id'
        );
      }

      // Transform to our Area interface
      const transformedArea: Area = {
        id: createdArea.id,
        name: createdArea.name || input.name,
        description: createdArea.description || input.description || '',
        coords: createdArea.coords || createdArea.points || input.coords,
        users: createdArea.users || [],
        circles: createdArea.circles || [],
        colorCode: createdArea.colorcode || createdArea.colorCode,
        createdBy: createdArea.createdBy,
        lastUpdatedBy: createdArea.lastUpdatedBy,
        createdAt: createdArea.createdAt,
        updatedAt: createdArea.updatedAt,
        isDeleted: createdArea.isDeleted || false,
      };

      return transformedArea;
    } else {
      console.error('No createArea data in response:', response);
      throw new Error('No data returned from createArea mutation');
    }
  } catch (error) {
    console.error('Error creating new area:', error);
    throw error;
  }
};

export const deleteArea = async (areaId: string): Promise<void> => {
  try {
    console.log('Deleting area:', areaId);

    // Format parameters for deletion - only need ID and deletion flag
    const params = {
      id: areaId,
      delete: true,
    };

    // Double stringify like lyferize does
    const stringifiedParams = JSON.stringify(params);
    console.log('Formatted params for delete area:', params);
    console.log('Stringified params for delete area:', stringifiedParams);

    const response: any = await API.graphql({
      query: createArea,
      variables: {
        request: JSON.stringify(stringifiedParams),
      },
    });

    if (response.data?.createArea) {
      // Parse the response like lyferize does
      const createAreaResult = response.data.createArea;
      const finalResult = JSON.parse(createAreaResult);

      // Check if this is a success response
      if (finalResult.statusCode === 200 || finalResult.message) {
        return; // Successfully deleted
      }

      // If we get here, deletion may have failed
      throw new Error(
        `Unexpected delete response: ${JSON.stringify(finalResult)}`
      );
    } else {
      throw new Error('No data returned from delete area mutation');
    }
  } catch (error) {
    console.error('Error deleting area:', error);
    throw error;
  }
};

// New functions for user and circle assignment
export const assignUsersToArea = async (
  areaId: string,
  users: any[],
  originalArea?: Area
): Promise<Area> => {
  try {
    console.log('Assigning users to area:', areaId, users);

    const updateInput: UpdateAreaInput = {
      id: areaId,
      users: users.map((user) => ({
        id: user.id,
        name: user.name,
        imageName: user.imageName,
        imageType: user.imageType,
        avatarFile: user.avatarFile,
        status: user.status,
        assignedAt: new Date().toISOString(),
      })),
    };

    return await updateArea(updateInput, originalArea);
  } catch (error) {
    console.error('Error assigning users to area:', error);
    throw error;
  }
};

export const assignCirclesToArea = async (
  areaId: string,
  circles: any[],
  originalArea?: Area
): Promise<Area> => {
  try {
    console.log('Assigning circles to area:', areaId, circles);

    const updateInput: UpdateAreaInput = {
      id: areaId,
      circles: circles.map((circle) => ({
        id: circle.id,
        name: circle.name,
        imageName: circle.imageName,
        imageType: circle.imageType,
        avatarFile: circle.avatarFile,
        assignedAt: new Date().toISOString(),
      })),
    };

    return await updateArea(updateInput, originalArea);
  } catch (error) {
    console.error('Error assigning circles to area:', error);
    throw error;
  }
};

export const updateAreaAssignments = async (
  areaId: string,
  users: any[],
  circles: any[],
  originalArea?: Area
): Promise<Area> => {
  try {
    console.log('Updating area assignments:', areaId, users, circles);

    const updateInput: UpdateAreaInput = {
      id: areaId,
      users: users.map((user) => ({
        id: user.id,
        name: user.name,
        imageName: user.imageName,
        imageType: user.imageType,
        avatarFile: user.avatarFile,
        status: user.status,
        assignedAt: new Date().toISOString(),
      })),
      circles: circles.map((circle) => ({
        id: circle.id,
        name: circle.name,
        imageName: circle.imageName,
        imageType: circle.imageType,
        avatarFile: circle.avatarFile,
        assignedAt: new Date().toISOString(),
      })),
    };

    return await updateArea(updateInput, originalArea);
  } catch (error) {
    console.error('Error updating area assignments:', error);
    throw error;
  }
};
